import { useEffect, useRef, useState } from 'react';
import { useGlobalState } from '../../Core/Contexts/globalStateContext';
import { ChatRoom, ChatRoomType } from '../../Core/Types/admin/chat';
import ChatRoomsList from './ChatRoomsList';
import ChatWindow from './ChatWindow';
import './Chat.scss';
import { ObjectType } from '../../Core/Types/base';
import { useSignalRChat } from '../../Core/Hooks/useSignalRChat';
import {
	useCreateDirectMessage,
	useGetAvailableChatRooms,
	useGetChatUsers,
	useGetNotDeliveredMessagesCountForUser,
} from '../../Core/Hooks/admin/useChat';
import ChatUsers from './ChatUsers';
import ChatLinkedObjects from './ChatLinkedObjects';
import { BizIcon } from '../../Core/Components/Mini/BizIcon';
import { UserBaseLegacy } from '../../Core/Types/user';
import { useTranslation } from 'react-i18next';

type ChatProps = {
	objectType?: ObjectType; // Optional: filter rooms by object type
	objectId?: number; // Optional: filter rooms by object ID
	initDMToUserId?: number; // Optional: user ID to initiate a direct message with
	showAllUsers?: boolean; // Whether to show the full user list or just users in the current context
	autoSetFocus?: boolean; // Whether to auto-focus the message input when a room is opened
	scrollToChatOnMobile?: boolean; // Whether to scroll embedded mobile chat into view when a room is opened
};

const Chat = ({
	objectType,
	objectId,
	initDMToUserId: pInitDMToUserId,
	showAllUsers,
	autoSetFocus,
	scrollToChatOnMobile,
}: ChatProps) => {
	const { t } = useTranslation();
	const {
		state: { user },
	} = useGlobalState();
	const chatContainerRef = useRef<HTMLDivElement>(null);
	const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
	const [initDMToUserId, setInitDMToUserId] = useState<number | undefined>(pInitDMToUserId); // Capture initial prop value
	const { data: allUsers } = useGetChatUsers(objectType, objectId); // "all" chat users for the current context
	const { data: availableRooms } = useGetAvailableChatRooms(objectType, objectId);
	const { data: notDeliveredMessagesCounts } = useGetNotDeliveredMessagesCountForUser(!!user?.Id);
	const chatFuncs = useSignalRChat(user, availableRooms ?? undefined, notDeliveredMessagesCounts ?? undefined);
	const {
		createDirectMessage,
		statusState: createDMStatusState,
		result: createDMResult,
	} = useCreateDirectMessage(objectType, objectId);
	const [selectRoomIdWhenAvailable, setSelectRoomIdWhenAvailable] = useState(0);

	useEffect(() => {
		if (typeof window === 'undefined' || !user?.Id) {
			return;
		}

		const chatContainer = chatContainerRef.current;
		if (!chatContainer) {
			return;
		}

		const mobileMediaQuery = window.matchMedia('(max-width: 960px)');
		const visualViewport = window.visualViewport;
		const updateMobileViewportHeight = () => {
			if (!mobileMediaQuery.matches) {
				chatContainer.style.removeProperty('--chat-mobile-viewport-height');
				return;
			}

			const viewportHeight = visualViewport?.height ?? window.innerHeight;
			chatContainer.style.setProperty('--chat-mobile-viewport-height', `${Math.round(viewportHeight)}px`);
		};

		updateMobileViewportHeight();
		visualViewport?.addEventListener('resize', updateMobileViewportHeight);
		window.addEventListener('resize', updateMobileViewportHeight);

		return () => {
			visualViewport?.removeEventListener('resize', updateMobileViewportHeight);
			window.removeEventListener('resize', updateMobileViewportHeight);
			chatContainer.style.removeProperty('--chat-mobile-viewport-height');
		};
	}, [user?.Id]);

	useEffect(() => {
		console.log('initDMToUserId effect', { initDMToUserId, availableRooms });
		// If initDMToUserId is provided, try to find or create a DM room with that user
		if (initDMToUserId && availableRooms) {
			const existingRoom = availableRooms.find(
				(r) => r.Type === ChatRoomType.DirectMessage && r.Members.some((m) => m.UserId === initDMToUserId)
			);
			if (existingRoom) {
				setSelectedRoom(existingRoom);
			} else {
				createDirectMessage(initDMToUserId);
			}
		}
	}, [initDMToUserId, availableRooms]);

	useEffect(() => {
		console.log('createDMResult effect', { createDMStatusState, createDMResult });
		if (createDMStatusState === 'SUCCESS' && createDMResult) {
			setSelectRoomIdWhenAvailable(createDMResult.Id);
			setInitDMToUserId(undefined);
		}
	}, [createDMStatusState, createDMResult]);

	useEffect(() => {
		console.log('selectRoomIdWhenAvailable effect', { selectRoomIdWhenAvailable, availableRooms });
		if (selectRoomIdWhenAvailable && availableRooms) {
			const newRoom = availableRooms.find((r) => r.Id === selectRoomIdWhenAvailable);
			if (newRoom) {
				setSelectedRoom(newRoom);
				setSelectRoomIdWhenAvailable(0);
			}
		}
	}, [selectRoomIdWhenAvailable, availableRooms]);

	useEffect(() => {
		if (!scrollToChatOnMobile || !selectedRoom || typeof window === 'undefined') {
			return;
		}

		if (!window.matchMedia('(max-width: 960px)').matches) {
			return;
		}

		const animationFrameId = window.requestAnimationFrame(() => {
			const chatHeader = chatContainerRef.current?.querySelector<HTMLElement>('.chat-header');

			if (!chatHeader) {
				return;
			}

			const headerHeightValue = window
				.getComputedStyle(document.documentElement)
				.getPropertyValue('--header-mobile-height');
			const headerHeight = parseFloat(headerHeightValue) || 0;
			const scrollTop = chatHeader.getBoundingClientRect().top + window.scrollY - headerHeight;

			window.scrollTo({
				top: Math.max(scrollTop, 0),
				behavior: 'smooth',
			});
		});

		return () => window.cancelAnimationFrame(animationFrameId);
	}, [scrollToChatOnMobile, selectedRoom]);

	if (!user || !user.Id) {
		return (
			<div className='biz-chat-container'>
				{!!user && !user.Id && (
					<div className='chat-error'>
						{t('CHAT.USER_MUST_BE_LOGGED_IN', {
							defaultValue: 'You must be logged in to use the chat system.',
						})}
					</div>
				)}
				{!user && <BizIcon icon='LOADING' />}
			</div>
		);
	}

	const onCreateDirectMessageDone = (newRoomId: number) => {
		setSelectRoomIdWhenAvailable(newRoomId);
	};

	const currentUserData = user as UserBaseLegacy;
	const chatLayoutClassName = `chat-layout ${selectedRoom ? 'chat-layout--room-selected' : 'chat-layout--room-list'}`;

	return (
		<div className='biz-chat-container' ref={chatContainerRef}>
			<div className={chatLayoutClassName}>
				<div className='chat-sidebar'>
					<ChatRoomsList
						onRoomSelect={setSelectedRoom}
						selectedRoom={selectedRoom}
						objectType={objectType}
						objectId={objectId}
						chatFuncs={chatFuncs}
						onCreateDirectMessageDone={onCreateDirectMessageDone}
						showRoomTypes={[ChatRoomType.Group]}
					/>
					<ChatRoomsList
						onRoomSelect={setSelectedRoom}
						selectedRoom={selectedRoom}
						objectType={objectType}
						objectId={objectId}
						chatFuncs={chatFuncs}
						onCreateDirectMessageDone={onCreateDirectMessageDone}
						showRoomTypes={[ChatRoomType.DirectMessage]}
					/>
					{showAllUsers && allUsers && (
						<>
							<div className='user-list-header'>
								<h4>{t('CHAT.PARTICIPANTS', { defaultValue: 'Participants' })}</h4>
							</div>
							<ChatUsers
								users={allUsers}
								currentUserId={user.Id}
								objectType={objectType}
								objectId={objectId}
								onCreateDirectMessageDone={onCreateDirectMessageDone}
								hideCurrentUser={true}
							/>
						</>
					)}

					{(objectType ?? ObjectType.None) === ObjectType.None && (
						<ChatLinkedObjects
							onRoomSelect={setSelectedRoom}
							selectedRoom={selectedRoom}
							chatFuncs={chatFuncs}
							onCreateDirectMessageDone={onCreateDirectMessageDone}
						/>
					)}
				</div>

				<div className='chat-main'>
					{selectedRoom ? (
						<ChatWindow
							chatRoom={selectedRoom}
							currentUser={currentUserData}
							onClose={() => setSelectedRoom(null)}
							chatFuncs={chatFuncs}
							autoSetFocus={autoSetFocus}
						/>
					) : (
						<div className='no-room-selected'>
							<h3>{t('CHAT.WELCOME_TO_CHAT', { defaultValue: 'Welcome to Chat' })}</h3>
							<p>
								{t('CHAT.SELECT_ROOM_TO_START', {
									defaultValue: 'Select a chat room from the sidebar to start chatting.',
								})}
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Chat;
